"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
var _a, _b;
Object.defineProperty(exports, "__esModule", { value: true });
exports.VMLiteralBytecodeCompiler = void 0;
const inversify_1 = require("inversify");
const IVMCompilerUtils_1 = require("../../../interfaces/vm-transformers/IVMCompilerUtils");
const VMSharedTypes_1 = require("../../../types/vm-transformers/VMSharedTypes");
const ServiceIdentifiers_1 = require("../../../container/ServiceIdentifiers");
const VMVariableResolver_1 = require("../VMVariableResolver");
const NodeGuards_1 = require("../../../node/NodeGuards");
const VMConstants_1 = require("../../VMConstants");
const VMOpCode_1 = require("../../../enums/vm-transformers/VMOpCode");
let VMLiteralBytecodeCompiler = class VMLiteralBytecodeCompiler {
    constructor(vmCompilerUtils, variableResolver) {
        this.vmCompilerUtils = vmCompilerUtils;
        this.variableResolver = variableResolver;
    }
    compileLiteral(node, context) {
        const builder = this.vmCompilerUtils.createInstructionBuilder(context);
        if (node.value === null) {
            builder.pushNull();
        }
        else if (node.value === undefined) {
            builder.pushUndefined();
        }
        else if ('regex' in node) {
            const regexNode = node;
            const pattern = regexNode.regex.pattern;
            const flags = regexNode.regex.flags;
            builder.newRegex(pattern, flags);
        }
        else if ('bigint' in node) {
            const bigintNode = node;
            const bigintString = `${bigintNode.bigint}n`;
            builder.loadConst(bigintString);
        }
        else {
            builder.loadConst(node.value);
        }
    }
    compileIdentifier(node, context) {
        var _a, _b, _c;
        const builder = this.vmCompilerUtils.createInstructionBuilder(context);
        const varName = node.name;
        if (varName === 'arguments') {
            if (!context.isArrowFunction) {
                builder.emitRaw({ op: VMOpCode_1.VMOpCode.ARGUMENTS, arg: 0 });
                return;
            }
            else {
                context.capturedArguments = true;
                context.capturedVars.add(VMConstants_1.VM_SPECIAL_VARS.capturedArguments);
                builder.loadFromEnvByName(VMConstants_1.VM_SPECIAL_VARS.capturedArguments);
                return;
            }
        }
        if ((_a = context.classExpressionNameAliases) === null || _a === void 0 ? void 0 : _a.has(varName)) {
            const internalKey = context.classExpressionNameAliases.get(varName);
            builder.loadFromEnvByName(internalKey);
            return;
        }
        if (context.functionName && varName === context.functionName) {
            const hasLocalShadow = context.localVariables.has(varName);
            const hasParameterShadow = context.parameters.has(varName);
            if (!hasLocalShadow && !hasParameterShadow) {
                builder.loadFromEnvByName(varName);
                return;
            }
            if (hasParameterShadow) {
                const paramIndex = context.parameters.get(varName);
                builder.loadArg(paramIndex);
                return;
            }
        }
        const resolution = this.variableResolver.resolve(varName, context);
        switch (resolution.location) {
            case VMSharedTypes_1.VariableLocation.Environment: {
                const isInParent = this.isVariableInParentScope(varName, context);
                if (isInParent && !this.hasLocalDeclaration(varName, context)) {
                    context.capturedVars.add(varName);
                    if (context.parentContext && !context.parentContext.envVars.has(varName)) {
                        context.parentContext.envVars.add(varName);
                    }
                }
                builder.loadFromEnv(resolution.index);
                break;
            }
            case VMSharedTypes_1.VariableLocation.Parameter:
                builder.loadArg(resolution.index);
                break;
            case VMSharedTypes_1.VariableLocation.Local: {
                const actualIndex = this.vmCompilerUtils.getActualLocalIndex(resolution.index, context);
                builder.loadLocal(actualIndex);
                break;
            }
            case VMSharedTypes_1.VariableLocation.TDZ: {
                builder.throwError('ReferenceError', `Cannot access '${varName}' before initialization`);
                break;
            }
            case VMSharedTypes_1.VariableLocation.Global: {
                const isInParent = this.isVariableInParentScope(varName, context);
                const isDeclaredInFunction = (_c = (_b = context.declaredVariables) === null || _b === void 0 ? void 0 : _b.has(varName)) !== null && _c !== void 0 ? _c : false;
                const shouldLoadFromEnv = isInParent || (context.parentContext !== undefined && isDeclaredInFunction);
                if (shouldLoadFromEnv) {
                    if (!this.hasLocalDeclaration(varName, context)) {
                        context.capturedVars.add(varName);
                        if (context.parentContext && !context.parentContext.envVars.has(varName)) {
                            context.parentContext.envVars.add(varName);
                        }
                    }
                    builder.loadFromEnvByName(varName);
                }
                else {
                    builder.loadGlobal(resolution.index);
                }
                break;
            }
        }
    }
    compileArrayExpression(node, context) {
        var _a, _b;
        const builder = this.vmCompilerUtils.createInstructionBuilder(context);
        builder.newArray();
        for (const element of node.elements) {
            if (element === null) {
                builder.pushUndefined();
                builder.arrayPush();
            }
            else if (NodeGuards_1.NodeGuards.isSpreadElementNode(element)) {
                (_a = context.dispatch) === null || _a === void 0 ? void 0 : _a.expression(element.argument);
                builder.arraySpreadPush();
            }
            else {
                (_b = context.dispatch) === null || _b === void 0 ? void 0 : _b.expression(element);
                builder.arrayPush();
            }
        }
    }
    compileObjectExpression(node, context) {
        const builder = this.vmCompilerUtils.createInstructionBuilder(context);
        builder.newObject();
        for (const property of node.properties) {
            this.compileObjectProperty(property, context);
        }
    }
    compileTemplateLiteral(node, context) {
        var _a;
        if (node.quasis.length === 0) {
            this.pushConstantString('', context);
            return;
        }
        if (node.expressions.length === 0) {
            this.pushConstantString((_a = node.quasis[0].value.cooked) !== null && _a !== void 0 ? _a : '', context);
            return;
        }
        this.compileTemplateLiteralParts(node, context);
    }
    compileTaggedTemplateExpression(node, context) {
        var _a, _b, _c;
        const builder = this.vmCompilerUtils.createInstructionBuilder(context);
        builder.newArray();
        for (const quasi of node.quasi.quasis) {
            const cookedValue = (_a = quasi.value.cooked) !== null && _a !== void 0 ? _a : quasi.value.raw;
            builder.loadConst(cookedValue);
            builder.arrayPush();
        }
        for (const expression of node.quasi.expressions) {
            (_b = context.dispatch) === null || _b === void 0 ? void 0 : _b.expression(expression);
        }
        (_c = context.dispatch) === null || _c === void 0 ? void 0 : _c.expression(node.tag);
        const argCount = 1 + node.quasi.expressions.length;
        builder.loadConst(argCount);
        builder.call(argCount);
    }
    compileObjectProperty(property, context) {
        var _a, _b;
        const builder = this.vmCompilerUtils.createInstructionBuilder(context);
        if (NodeGuards_1.NodeGuards.isSpreadElementNode(property)) {
            (_a = context.dispatch) === null || _a === void 0 ? void 0 : _a.expression(property.argument);
            builder.objectSpread();
            return;
        }
        const prop = property;
        if (prop.kind === 'get') {
            this.compileObjectGetter(prop, context);
            return;
        }
        if (prop.kind === 'set') {
            this.compileObjectSetter(prop, context);
            return;
        }
        builder.dup();
        if (prop.computed) {
            (_b = context.dispatch) === null || _b === void 0 ? void 0 : _b.expression(prop.key);
            if (!this.compileObjectPropertyValue(prop, context)) {
                builder.pop();
                builder.pop();
                return;
            }
            builder.setComputed();
            builder.pop();
            return;
        }
        if (!this.compileObjectPropertyValue(prop, context)) {
            builder.pop();
            return;
        }
        this.compileObjectPropertyKey(prop, context);
        builder.pop();
    }
    compileObjectGetter(prop, context) {
        this.compileObjectAccessor(prop, context, true);
    }
    compileObjectSetter(prop, context) {
        this.compileObjectAccessor(prop, context, false);
    }
    compileObjectAccessor(prop, context, isGetter) {
        var _a, _b, _c;
        const builder = this.vmCompilerUtils.createInstructionBuilder(context);
        if (!NodeGuards_1.NodeGuards.isFunctionExpressionNode(prop.value)) {
            return;
        }
        if (prop.computed) {
            (_a = context.dispatch) === null || _a === void 0 ? void 0 : _a.expression(prop.key);
            (_b = context.dispatch) === null || _b === void 0 ? void 0 : _b.expression(prop.value);
            if (isGetter) {
                builder.emitRaw({ op: VMOpCode_1.VMOpCode.DEFINE_GETTER_COMPUTED });
            }
            else {
                builder.emitRaw({ op: VMOpCode_1.VMOpCode.DEFINE_SETTER_COMPUTED });
            }
            return;
        }
        (_c = context.dispatch) === null || _c === void 0 ? void 0 : _c.expression(prop.value);
        const propName = this.getPropertyName(prop);
        if (!propName) {
            builder.pop();
            return;
        }
        const propNameIndex = context.constantPool.add(propName);
        if (isGetter) {
            builder.defineGetter(propNameIndex);
        }
        else {
            builder.defineSetter(propNameIndex);
        }
    }
    compileObjectPropertyValue(prop, context) {
        var _a, _b, _c;
        if (prop.method) {
            if (NodeGuards_1.NodeGuards.isFunctionExpressionNode(prop.value)) {
                (_a = context.dispatch) === null || _a === void 0 ? void 0 : _a.expression(prop.value);
                return true;
            }
            return false;
        }
        if (prop.shorthand) {
            if (NodeGuards_1.NodeGuards.isIdentifierNode(prop.key)) {
                (_b = context.dispatch) === null || _b === void 0 ? void 0 : _b.expression(prop.key);
                return true;
            }
            return false;
        }
        (_c = context.dispatch) === null || _c === void 0 ? void 0 : _c.expression(prop.value);
        return true;
    }
    compileObjectPropertyKey(prop, context) {
        var _a;
        const builder = this.vmCompilerUtils.createInstructionBuilder(context);
        if (prop.computed) {
            (_a = context.dispatch) === null || _a === void 0 ? void 0 : _a.expression(prop.key);
            builder.setComputed();
            return;
        }
        let propName;
        if (NodeGuards_1.NodeGuards.isIdentifierNode(prop.key)) {
            propName = prop.key.name;
        }
        else if (NodeGuards_1.NodeGuards.isLiteralNode(prop.key)) {
            propName = String(prop.key.value);
        }
        else {
            builder.pop();
            builder.pop();
            return;
        }
        const propIndex = context.constantPool.add(propName);
        builder.setProperty(propIndex);
    }
    getPropertyName(prop) {
        if (prop.computed) {
            return null;
        }
        if (NodeGuards_1.NodeGuards.isIdentifierNode(prop.key)) {
            return prop.key.name;
        }
        else if (NodeGuards_1.NodeGuards.isLiteralNode(prop.key)) {
            return String(prop.key.value);
        }
        return null;
    }
    compileTemplateLiteralParts(node, context) {
        var _a;
        const firstQuasi = (_a = node.quasis[0].value.cooked) !== null && _a !== void 0 ? _a : '';
        this.pushConstantString(firstQuasi, context);
        for (let i = 0; i < node.expressions.length; i++) {
            this.compileTemplateLiteralExpression(i, node, context);
        }
    }
    compileTemplateLiteralExpression(index, node, context) {
        var _a, _b;
        const builder = this.vmCompilerUtils.createInstructionBuilder(context);
        (_a = context.dispatch) === null || _a === void 0 ? void 0 : _a.expression(node.expressions[index]);
        builder.add();
        const quasi = (_b = node.quasis[index + 1].value.cooked) !== null && _b !== void 0 ? _b : '';
        if (quasi.length > 0) {
            this.pushConstantString(quasi, context);
            builder.add();
        }
    }
    pushConstantString(value, context) {
        const builder = this.vmCompilerUtils.createInstructionBuilder(context);
        builder.loadConst(value);
    }
    isVariableInParentScope(varName, context) {
        let currentContext = context.parentContext;
        while (currentContext) {
            if (currentContext.parameters.has(varName) && !currentContext.parametersInTDZ.has(varName)) {
                return true;
            }
            if (currentContext.localVariables.has(varName)) {
                return true;
            }
            for (const [key] of currentContext.localVariables) {
                if (key.startsWith(`${varName}${VMConstants_1.VM_SEPARATORS.blockScope}`)) {
                    return true;
                }
            }
            currentContext = currentContext.parentContext;
        }
        return false;
    }
    hasLocalDeclaration(varName, context) {
        var _a, _b;
        return (context.localVariables.has(varName) ||
            context.parameters.has(varName) ||
            ((_b = (_a = context.perIterationLoopVars) === null || _a === void 0 ? void 0 : _a.has(varName)) !== null && _b !== void 0 ? _b : false) ||
            Array.from(context.localVariables.keys()).some((key) => key.startsWith(`${varName}${VMConstants_1.VM_SEPARATORS.blockScope}`)));
    }
};
exports.VMLiteralBytecodeCompiler = VMLiteralBytecodeCompiler;
exports.VMLiteralBytecodeCompiler = VMLiteralBytecodeCompiler = __decorate([
    (0, inversify_1.injectable)(),
    __param(0, (0, inversify_1.inject)(ServiceIdentifiers_1.ServiceIdentifiers.IVMCompilerUtils)),
    __param(1, (0, inversify_1.inject)(ServiceIdentifiers_1.ServiceIdentifiers.IVMVariableResolver)),
    __metadata("design:paramtypes", [typeof (_a = typeof IVMCompilerUtils_1.IVMCompilerUtils !== "undefined" && IVMCompilerUtils_1.IVMCompilerUtils) === "function" ? _a : Object, typeof (_b = typeof VMVariableResolver_1.VMVariableResolver !== "undefined" && VMVariableResolver_1.VMVariableResolver) === "function" ? _b : Object])
], VMLiteralBytecodeCompiler);
